import { Box, SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import {
	getActiveStep,
	getActiveTicket,
	getEditMode,
	getIsReportLoading,
	getIsTestCasesGenerating,
	getIsTicketShare,
	getIsUseCasesGenerating,
} from 'store/ticket-service/selector';
import { setActiveStep, setActiveSubStage, setReportAnIssueMode } from 'store/ticket-service/ticketSlice';
import { setIsOpenUserProfile } from 'store/user-service/userSlice';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { ROUTES } from 'core/constants/routes';

import { useEventBus } from 'utils/EventBus';

import { GeneralStepConversationTopics, NamespacesList, TicketStages } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';

import { ReactComponent as FunctionalRequirementsStep } from 'assets/icons/functional-requirements-step.svg';
import { ReactComponent as NotificationsStep } from 'assets/icons/notifications-step.svg';
import { ReactComponent as ReportStep } from 'assets/icons/report-step.svg';
import { ReactComponent as TestCasesStep } from 'assets/icons/test-cases-step.svg';
import { ReactComponent as UseCasesStep } from 'assets/icons/use-cases-step.svg';

import { TicketStep } from './TicketStep';

export const TicketStepper = () => {
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const navigate = useNavigate();
	const location = useLocation();

	const switchNotify = {
		title: commonTranslate('errors.failedToSwitchStepTitle'),
		description: commonTranslate('errors.failedToSwitchStepDescription'),
	};

	const dispatch = useAppDispatch();
	const activeStep = useSelector(getActiveStep);
	const { addEventListener, removeEventListener } = useEventBus();
	const activeTicket = useSelector(getActiveTicket);
	const editMode = useSelector(getEditMode);
	const isUseCasesGenerating = useSelector(getIsUseCasesGenerating);
	const isTestCasesGenerating = useSelector(getIsTestCasesGenerating);
	const isReportLoading = useSelector(getIsReportLoading);
	const isTicketShared = useSelector(getIsTicketShare);

	// Fonction pour naviguer vers /brd si on n'est pas déjà dessus
	const navigateToBrdIfNeeded = () => {
		if (location.pathname !== ROUTES.BRD) {
			// Si on est sur /support, désactiver le mode reportAnIssue
			if (location.pathname === ROUTES.SUPPORT) {
				dispatch(setReportAnIssueMode(false));
			}
			// Si on est sur /settings, fermer le profil utilisateur
			if (location.pathname.startsWith(ROUTES.SETTINGS)) {
				dispatch(setIsOpenUserProfile(false));
			}
			navigate(ROUTES.BRD);
		}
	};

	const [currentSubStageLabel, setCurrentSubStageLabel] = useState<string>(
		GeneralStepConversationTopics[0]
	);

	useEffect(() => {
		addEventListener('subStageChange', handleSubStageChange);

		return () => {
			removeEventListener('subStageChange', handleSubStageChange);
		};
	}, []);

	// user always can navigate to general step

	const isGeneralStepReady = useMemo(() => {
		return true;
	}, [activeTicket?.id]);

	const handleAutoSave = (step: TicketStages) => {
		if (step > (activeTicket?.currentStep || 0) && !isTicketShared) dispatch(triggerTicketSave());
	};

	const setGeneralStep = () => {
		if (isGeneralStepReady) {
			navigateToBrdIfNeeded();
			handleAutoSave(TicketStages.general);
			if (!editMode) dispatch(setActiveStep(TicketStages.general));
			else errorNotify(switchNotify.title, switchNotify.description);
		}
	};

	const areUseCasesIdsAssigned = useMemo(
		() => activeTicket?.useCases?.filter((useCase) => useCase?.id)?.length,
		[activeTicket?.useCases]
	);

	// user can navigate to use cases step only when user stories are generated

	const isUseCasesStepReady = useMemo(() => {
		return (
			isGeneralStepReady &&
			activeTicket?.ticketSummaries?.length &&
			activeTicket?.ticketSummaries?.every(
				// (summary) => summary.summaryAcceptanceCriteria?.length && summary.summaryScenarios?.length
				(summary) => summary.summaryScenarios?.length

			) &&
			(areUseCasesIdsAssigned || isUseCasesGenerating)
		);
	}, [
		activeTicket?.id,
		activeTicket?.ticketSummaries,
		isGeneralStepReady,
		areUseCasesIdsAssigned,
		isUseCasesGenerating,
	]);

	const setUseCasesStep = () => {
		if (isUseCasesStepReady) {
			navigateToBrdIfNeeded();
			handleAutoSave(TicketStages.useCases);
			if (!editMode) dispatch(setActiveStep(TicketStages.useCases));
			else errorNotify(switchNotify.title, switchNotify.description);
		}
	};

	const isTestCasesReady = useMemo(() => {
		return isUseCasesStepReady && (activeTicket?.userStoryTests?.length || isTestCasesGenerating);
	}, [activeTicket?.id, activeTicket?.userStoryTests, isUseCasesStepReady, isTestCasesGenerating]);

	const setTestCasesStep = () => {
		if (isTestCasesReady) {
			navigateToBrdIfNeeded();
			handleAutoSave(TicketStages.testCases);
			if (!editMode) dispatch(setActiveStep(TicketStages.testCases));
			else errorNotify(switchNotify.title, switchNotify.description);
		}
	};

	// user can navigate to notifications step only when use cases are generated

	const isNotificationsStepVisited = useMemo(
		() =>
			Boolean(
				activeTicket?.messages?.filter((message) => message.stage === TicketStages.notifications)
					?.length
			),
		[activeTicket?.messages]
	);

	const isNotificationsStepReady = useMemo(() => {
		return isTestCasesReady && (activeTicket?.notifications?.length || isNotificationsStepVisited);
	}, [activeTicket?.id, activeTicket?.notifications, isTestCasesReady, isNotificationsStepVisited]);

	const setNotificationsStep = () => {
		if (isNotificationsStepReady) {
			navigateToBrdIfNeeded();
			handleAutoSave(TicketStages.notifications);
			if (!editMode) dispatch(setActiveStep(TicketStages.notifications));
			else errorNotify(switchNotify.title, switchNotify.description);
		}
	};

	// user can navigate to report step only when at least one notification is generated

	// const isReportStepReady = useMemo(() => {
	// 	return (
	// 		isNotificationsStepReady &&
	// 		((activeTicket?.businessImpact?.length && activeTicket?.bannersJson?.length) ||
	// 			isReportLoading)
	// 	);
	// }, [
	// 	activeTicket?.id,
	// 	activeTicket?.businessImpact,
	// 	activeTicket?.bannersJson,
	// 	isNotificationsStepReady,
	// 	isReportLoading,
	// ]);


	// Report step is always ready, regardless of other steps
	const isReportStepReady = useMemo(() => {
		// Report step is always ready, regardless of other steps
		return true;
	  }, []);

	const setReportStep = () => {
		//initial conditions
		// if (
		// 	isReportStepReady ||
		// 	activeTicket?.permissions
		// 		?.map((permission) => permission.ticketCurrentStep)
		// 		?.includes(TicketStages.report)
		// ) {
		// 	handleAutoSave(TicketStages.report);
		// 	if (!editMode) dispatch(setActiveStep(TicketStages.report));
		// 	else errorNotify(switchNotify.title, switchNotify.description);
		// }

		navigateToBrdIfNeeded();
		handleAutoSave(TicketStages.report);
		if (!editMode) dispatch(setActiveStep(TicketStages.report));
		else errorNotify(switchNotify.title, switchNotify.description);
	};

	const handleSubStageChange = (payload: any) => {
		setCurrentSubStageLabel(payload);
	};

	const handleChange = (event: SelectChangeEvent) => {
		setCurrentSubStageLabel(event.target.value as string);
	};

	const isStepActive = (step: TicketStages) => {
		return step === activeStep;
	};

	const isStepPermissionGiven = (stepName: TicketStages, originalCondition: boolean) => {
		if (
			activeTicket?.permissions?.length &&
			!activeTicket.permissions.map((permission) => permission.ticketCurrentStep).includes(null)
		) {
			const allStagePermission = activeTicket.permissions.map(
				(permission) => permission.ticketCurrentStep
			);

			return allStagePermission.includes(stepName);
		}

		return originalCondition;
	};

	const isSubStagePermissionGiven = (subStageName: GeneralStepConversationTopics) => {
		if (activeTicket?.permissions?.length) {
			const currentSubStageEnum = GeneralStepConversationTopics[
				subStageName
			] as unknown as GeneralStepConversationTopics;

			const stagesEnums = activeTicket.permissions.map(
				(permission) => permission.ticketCurrentStep
			);

			const subStagesEnums = activeTicket.permissions.map((permission) => permission.subStageEnum);

			return (
				(stagesEnums.includes(null) || stagesEnums.includes(TicketStages.general)) &&
				(subStagesEnums.includes(null) || subStagesEnums.includes(currentSubStageEnum))
			);
		}

		return true;
	};

	const subSteps = useMemo(
		() =>
			Object.entries(GeneralStepConversationTopics)
				.filter((item) => item[0].length === 1)
				.map((item) => ({
					key: item[1],
					title: item[1],
					isItemReady: isTicketShared
						? isSubStagePermissionGiven(item[1] as GeneralStepConversationTopics)
						: true,
					onClick: () => dispatch(setActiveSubStage(+item[0])),
				})),
		[isTicketShared]
	);

	const handleClickSubStageStep = () => {
		if (!isStepActive(TicketStages.general)) setGeneralStep();
		else navigateToBrdIfNeeded();
	};

	const steps = [
		{
			isStepReady: isTicketShared
				? isStepPermissionGiven(TicketStages.general, isGeneralStepReady)
				: isGeneralStepReady,
			isStepActive: isStepActive(TicketStages.general),
			onClick: handleClickSubStageStep,
			selectValue: currentSubStageLabel,
			selectItems: subSteps,
			areSubStepsOpen: false,
			selectOnChange: handleChange,
			title: 'stepper.generalStep',
			icon: <FunctionalRequirementsStep />,
		},
		{
			isStepReady: isTicketShared
				? isStepPermissionGiven(TicketStages.useCases, Boolean(isUseCasesStepReady))
				: isUseCasesStepReady,
			isStepActive: isStepActive(TicketStages.useCases),
			onClick: setUseCasesStep,
			title: 'stepper.useCasesStep',
			icon: <UseCasesStep />,
		},
		{
			isStepReady: isTicketShared
				? isStepPermissionGiven(TicketStages.testCases, Boolean(isTestCasesReady))
				: isTestCasesReady,
			isStepActive: isStepActive(TicketStages.testCases),
			onClick: setTestCasesStep,
			title: 'stepper.testCasesStep',
			icon: <TestCasesStep />,
		},
		{
			isStepReady: isTicketShared
				? isStepPermissionGiven(TicketStages.notifications, Boolean(isNotificationsStepReady))
				: isNotificationsStepReady,
			isStepActive: isStepActive(TicketStages.notifications),
			onClick: setNotificationsStep,
			title: 'stepper.notificationsStep',
			icon: <NotificationsStep />,
		},
		{
			isStepReady: isTicketShared
				? isStepPermissionGiven(TicketStages.report, Boolean(isReportStepReady))
				: isReportStepReady,
			// isStepReady: true,
			isStepActive: isStepActive(TicketStages.report),
			// isStepActive: true,
			onClick: setReportStep,
			title: 'stepper.reportStep',
			icon: <ReportStep />,
		},
	];

	return (
		<Box
			className="rtl-able"
			sx={{
				marginTop: '16px',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				width: '100%',
			}}
		>
			{steps.map((step) => (
				<TicketStep
					isStepReady={step.isStepReady}
					isStepActive={step.isStepActive}
					onClick={step.onClick}
					title={step.title}
					icon={step.icon}
					key={step.title}
					selectValue={step.selectValue}
					selectItems={step.selectItems}
					selectOnChange={step.selectOnChange}
				/>
			))}
		</Box>
	);
};
